import React, { FC, useState } from 'react';
import { INormalSeasonGames } from '../../../scripts/dataProcessors';
import { TeamID } from '../../../scripts/models/common';
import { PartialTeam, TeamModel } from '../../../scripts/models/models';
import { cumSum, max, min } from '../../../scripts/utility';
import TeamIcon from '../../common/teamIcon/TeamIcon';
import TeamSelector from '../../common/teamSelector/TeamSelector';

import './WinsOver500Chart.scss';

interface IProps {
    teams: TeamModel[],
    games: INormalSeasonGames,
    title: string,
    currentDay: number
}

interface teamLine {
    color: string,
    points: string,
    className: string,
    onTop: boolean
}

interface teamOpponent {
    team: PartialTeam,
    day: number,
    wins: number,
    className: string,
}

interface teamOpponents {
    team: TeamModel,
    opponents: teamOpponent[]
}

const MAX_DAY_NORMAL_SEASON = 90;

const WinsOver500Chart: FC<IProps> = (props) => {
    const { teams, games: gamesData, title, currentDay } = props

    const [highlightTeam, setHighlightTeam] = useState<TeamID>();

    // Process the team records to set bounds
    let maxRecord = 0;
    let minRecord = 0;
    let maxDay = 0
    const teamDeltas: Record<TeamID, number[]> = {}
    teams.forEach((team) => {
        const gameModels = gamesData.gamesByTeam[team.id].filter(game => (game.day < MAX_DAY_NORMAL_SEASON) && (game.day <= currentDay))
        if (gameModels.length > 0) {

            const deltas = gameModels.map(game => game.complete ? (game.gameWinnerId === team.id ? 1 : -1) : 0);
            const sums = cumSum(deltas);
            maxRecord = Math.max(maxRecord, sums.reduce(max))
            minRecord = Math.max(minRecord, -1 * sums.reduce(min))
            teamDeltas[team.id] = deltas;
            maxDay = Math.max(maxDay, gameModels[gameModels.length - 1].day)
        }
    })
    maxRecord = Math.max(0, maxRecord)
    minRecord = Math.max(0, minRecord)
    const weeksToShow = Math.min(10, (Math.ceil(maxDay / 9) + 1))

    // Turn bound data into scaling
    const xScale = Math.floor(6 * 10 * 2 / weeksToShow) / 2;
    let yScale = 5;
    const baseHeight = ((maxRecord + minRecord + 6))
    if (baseHeight < 100) {
        yScale = Math.min(xScale, Math.floor(400 / baseHeight));
    }

    const xBuffer = 20;
    const yBuffer = 6 * yScale;
    let winningHalf = (maxRecord * yScale) + yBuffer - 0.5;
    let losingHalf = (minRecord * yScale) + yBuffer;



    if (winningHalf < 230) {
        winningHalf = Math.max(Math.min(230, 460 - losingHalf), winningHalf)
    }

    if (losingHalf < 230) {
        losingHalf = Math.max(Math.min(230, 460 - winningHalf), losingHalf)
    }
    const width = (((90) * 6) + xBuffer) * 2; // Full games use 2 segments of xscale
    const height = winningHalf + losingHalf;

    // Build team lines
    const teamLines: teamLine[] = [];
    teams.forEach((team) => {
        const yDeltas = teamDeltas[team.id];
        if (yDeltas === undefined) {
            return;
        }
        const pointDeltas = yDeltas.map(val => val === 1 ? [2 * xScale, -1 * yScale] : val === -1 ? [2 * xScale, yScale] : [2 * xScale, 0])

        if (pointDeltas[pointDeltas.length - 1][1] === 0) {
            pointDeltas[pointDeltas.length - 1][0] = xScale;
        }

        const points = [[xBuffer, winningHalf]]
        let currentPt = [xBuffer, winningHalf]
        pointDeltas.forEach(delta => {
            currentPt = [currentPt[0] + delta[0], currentPt[1] + delta[1]]
            points.push(currentPt);
        })
        teamLines.push({
            color: team.primaryColor,
            points: points.map(point => point.join(",")).join(" "),
            className: (highlightTeam !== undefined) ? (highlightTeam === team.id ? "emphasize" : "deemphasize") : "",
            onTop: (highlightTeam !== undefined) && (highlightTeam === team.id)
        })
    })


    // Build team opponent data
    const teamsWithOpponents: teamOpponents[] = [];
    teams.forEach(team => {
        const opponents: teamOpponent[] = [];
        const gameModels = gamesData.gamesByTeam[team.id].filter(game => (game.day < (9 * weeksToShow)))
        const deltas = teamDeltas[team.id];
        const winsOverTime = [0].concat(cumSum(deltas));
        let lastScore = 0;
        gameModels.forEach((game) => {
            const day = game.day;
            if (day % 3 === 0) {
                const opponent = (game.awayTeam.id === team.id) ? game.homeTeam : game.awayTeam;
                let sideMax = Math.max(winsOverTime[day], winsOverTime[day + 3] ?? -999);
                let middleMax = Math.max(winsOverTime[day + 1] ?? -999, winsOverTime[day + 2] ?? -999)
                let scores = Math.max(sideMax - 1, middleMax);
                if (!isNaN(scores)) {
                    lastScore = scores
                } else {
                    scores = lastScore
                }

                opponents.push({
                    day: day,
                    team: opponent,
                    wins: scores,
                    className: (game.complete || game.started) ? "" : "future"
                })
            }
        })
        teamsWithOpponents.push({
            team,
            opponents
        })
    })




    return <div className='WinsOverChart'>
        <div className='teamLines'>
            <div className='contents'>

                <svg width={width} height={height}>
                    {
                        (new Array((3 * weeksToShow) + 1)).fill("").map((_el, idx) => {
                            const x = xBuffer + (6 * idx * xScale) - 0.5;
                            const isMajor = (idx % 3) === 0;
                            const className = isMajor ? 'majorLine' : 'minorLine';
                            return <polyline key={idx} className={className} points={`${x},0 ${x},${height}`} />
                        })
                    }
                    <polyline className='midLine' points={`0,${winningHalf} ${width},${winningHalf}`} />
                    {
                        // These lines are underneath
                        teamLines.map((line, idx) => {
                            return <polyline className={`teamLine below ${line.className}`} key={idx} points={line.points} style={{ stroke: line.color }} />


                        })
                    }
                    {
                        // These lines are above
                        teamLines.map((line, idx) => {

                            return <polyline className={`teamLine onTop  ${line.className}`} key={idx} points={line.points} style={{ stroke: line.color }} />


                        })
                    }
                </svg>
                {
                    teamsWithOpponents.map(data => {
                        const show = highlightTeam === data.team.id

                        const top = 20 + winningHalf;
                        const style = {
                            top,
                            opacity: show ? 1 : 0
                        }
                        return (
                            <div className="opponents" key={data.team.id} style={style}>
                                {data.opponents.map((matchupData, idx) => {
                                    const team = matchupData.team;
                                    const style = {
                                        top: winningHalf - (matchupData.wins * yScale) - 5 + (show ? 0 : -4),
                                        left: (xBuffer + 10) + (3 * xScale) + (2 * xScale * matchupData.day) - 15
                                    }
                                    return (
                                        <div className={`opponent ${matchupData.className}`} key={idx} style={style}>

                                            <TeamIcon emoji={team.emoji} color={team.primaryColor} shape="Small Circle" />
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })
                }
                {
                    Array(weeksToShow + 1).fill("").map((_el, idx) => {
                        const x = 10 + xBuffer + (18 * idx * xScale);
                        const y = 35 + height;
                        const style = { left: x, top: y }
                        return (
                            <div className='majorMark' style={style} key={idx}>
                                {9 * idx}
                            </div>
                        )
                    })
                }
                <div className='title serif'>
                    {title}
                </div>
                <TeamSelector currentTeam={highlightTeam} setter={setHighlightTeam} teams={teams} displayMode="Vertical Compact" triggerMethod='Hover' />
            </div>
        </div>
    </div>
}

export default WinsOver500Chart;
